import {
  computed,
  inject,
  type InjectionKey,
  type MaybeRefOrGetter,
  provide,
  readonly,
  type Ref,
  ref,
  watch
} from 'vue'

import type { LocationGroupsRepository } from '../application/LocationGroupsRepository'
import type { LocationGroup } from '../domain/locationGroup'
import { useLocationGroupsAccess } from './useLocationGroupsAccess'

export interface LocationGroupsUiStore {
  id: string
  name: string
}

export interface LocationGroupsUiContext {
  repository: LocationGroupsRepository
  canManageLocationGroups: Readonly<Ref<boolean>>
  managerStore: Readonly<Ref<LocationGroupsUiStore | null>>
  managerVisible: Ref<boolean>
  confirmDelete: (group: LocationGroup) => Promise<boolean>
  confirmReplacement: () => Promise<boolean>
  openManager: (store: LocationGroupsUiStore) => void
}

interface LocationGroupsUiOptions {
  repository: LocationGroupsRepository
  roles: MaybeRefOrGetter<readonly string[]>
  confirmDelete: LocationGroupsUiContext['confirmDelete']
  confirmReplacement: LocationGroupsUiContext['confirmReplacement']
}

export const LOCATION_GROUPS_UI_KEY: InjectionKey<LocationGroupsUiContext> = Symbol('LocationGroupsUiContext')

export function provideLocationGroupsUiContext(options: LocationGroupsUiOptions): LocationGroupsUiContext {
  const { canManageLocationGroups } = useLocationGroupsAccess(options.roles)
  const managerStore = ref<LocationGroupsUiStore | null>(null)
  const managerVisible = ref(false)

  function openManager(store: LocationGroupsUiStore): void {
    if (!canManageLocationGroups.value) return
    managerStore.value = store
    managerVisible.value = true
  }

  watch(canManageLocationGroups, canManage => {
    if (!canManage) managerVisible.value = false
  })

  const context: LocationGroupsUiContext = {
    repository: options.repository,
    canManageLocationGroups: readonly(computed(() => canManageLocationGroups.value)),
    managerStore: readonly(managerStore),
    managerVisible,
    confirmDelete: options.confirmDelete,
    confirmReplacement: options.confirmReplacement,
    openManager
  }
  provide(LOCATION_GROUPS_UI_KEY, context)
  return context
}

export function useLocationGroupsUiContext(): LocationGroupsUiContext {
  const context = inject(LOCATION_GROUPS_UI_KEY)
  if (!context) throw new Error('LocationGroupsUiContext not provided.')
  return context
}
